import { Dispatch, SetStateAction, useEffect, useState } from "react";

import {
  Button,
  Card,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Textarea,
  Text,
  Tooltip,
  ButtonGroup,
  CloseButton,
  Box,
} from "@chakra-ui/react";
import { QuestionIcon } from "@chakra-ui/icons";
import toast from "react-hot-toast";

import { textStyle } from "theme/names";
import { Filter } from "components/search-bar/filter";
import { getAccount, updateUser } from "utils/sessionHelper";
import { KeywordsMultiselect } from "components/keywords-multiselect";
import { useSession } from "next-auth/react";
import { InputCountrySelect } from "./input-country-select";
import { Option, InputSelectRadio } from "./input-select";
import { generateDescription } from "utils/staticStringGeneration";
import {
  carrouselOptions,
  hashtagOptions,
  lastInputDateOptions,
  plateformOptions,
} from "./static-values";
import { MutableRefObject } from "react";

const TitleWithTooltip = ({ title, tooltip }) => {
  return (
    <FormLabel textStyle={textStyle["body.medium.standard"]} mt={3}>
      {title}
      <Tooltip hasArrow label={tooltip}>
        <QuestionIcon color="gray.400" mt={-2} ml={2} />
      </Tooltip>
    </FormLabel>
  );
};

interface SettingsFiltersProps {
  setNoHashtag: Dispatch<SetStateAction<number>>;
  noHashtag: number;

  setIncludeCarousel: Dispatch<SetStateAction<number>>;
  includeCarousel: number;

  setPlateformFilter: Dispatch<SetStateAction<number>>;
  plateformFilter: number;

  exludedUsers: Array<string>;
  setExcludedUsers: Dispatch<SetStateAction<Array<string>>>;

  setCountrySelected: Dispatch<SetStateAction<Array<string>>>;
  countrySelected: Array<string>;

  setMaximumDateFilter: Dispatch<SetStateAction<Option>>;
  maximumDateFilter: Option;

  setHasFetched: Dispatch<SetStateAction<boolean>>;
  closeSettings: () => void;

  clearCache?: MutableRefObject<boolean>;
}

export const SettingsFilters = (props: SettingsFiltersProps) => {
  const { data: session } = useSession() ?? {};
  const [userEmail, setUserEmail] = useState("");
  const [userWho, setUserWho] = useState("");
  const [userTopics, setUserTopics] = useState("");
  const [userKeywords, setUserKeywords] = useState<Array<string>>([]);

  useEffect(() => {
    if (getAccount(session)?.id) {
      setUserEmail(getAccount(session).email);
      setUserWho(getAccount(session).who);
      setUserTopics(getAccount(session).topics);
      setUserKeywords(getAccount(session)?.keywords);
    }
  }, [session]);

  const saveUpdates = async () => {
    if (!userWho || userWho?.length < 10 || userWho?.length > 300) {
      toast.error("please tell us who you are in 10 to 300 characters");
      return;
    }
    if (!userTopics || userTopics?.length < 10 || userTopics?.length > 300) {
      toast.error(
        "please tell us what you usually talk about in 10 to 300 characters"
      );
      return;
    }

    // Since there's a lot of props passed
    // I choosed to keep the props. prefix to avoid confusion
    props.setNoHashtag(props.noHashtag);
    props.setIncludeCarousel(props.includeCarousel);
    props.setPlateformFilter(props.plateformFilter);
    props.setHasFetched(false);
    if (props.clearCache?.current !== undefined) {
      props.clearCache.current = true;
    }

    await updateUser(
      session,
      {
        who: userWho,
        keywords: userKeywords,
        description: generateDescription(
          userWho,
          userTopics,
          getAccount(session)?.languagePreferred
        ),
        topics: userTopics,
        plateformFilter: props.plateformFilter,
        hastagFilter: props.noHashtag,
        carrouselFilter: props.includeCarousel,
        nbMaxDaysOld: parseInt(props.maximumDateFilter?.value),
        exludedUsers: props.exludedUsers,
        countryFilter: props.countrySelected,
      },
      true,
      true
    );

    props.closeSettings();
  };

  const resetSettings = async () => {
    props.setNoHashtag(-1);
    props.setIncludeCarousel(0);
    props.setPlateformFilter(0);
    props.setHasFetched(false);
    props.setExcludedUsers([]);
    props.setMaximumDateFilter(lastInputDateOptions[1]);

    props.setCountrySelected([]);

    await updateUser(
      session,
      {
        plateformFilter: props.plateformFilter,
        hastagFilter: props.noHashtag,
        carrouselFilter: props.includeCarousel,
        exludedUsers: [],
        nbMaxDaysOld: 0,
        countryFilter: [],
      },
      true,
      true
    );
  };

  return (
    <Card width="100%" mt="10" p="8">
      <Text
        textStyle={textStyle["h2.bold.standard"]}
        color="#2D64BC"
        size="2xl"
      >
        Your account settings
      </Text>
      <Flex
        gap="3"
        mt="5"
        wrap={{ base: "wrap", md: "nowrap" }}
        justifyContent="space-between"
      >
        <FormControl>
          <TitleWithTooltip
            title="Search keywords"
            tooltip="Define your main keywords to improve the auto search and the relevance of the daily emails"
          />
          <HStack>
            <KeywordsMultiselect
              keywords={userKeywords}
              setKeywords={setUserKeywords}
            />
          </HStack>
        </FormControl>

        <FormControl>
          <TitleWithTooltip
            title=" Personal description"
            tooltip="Tell us who you are to improve the results personalization"
          />
          <Textarea
            value={userWho}
            onChange={(e) => setUserWho(e.target.value)}
            maxW={500}
            placeholder="Maker"
            borderRadius="lg"
          />
        </FormControl>
      </Flex>

      <Flex>
        <FormControl>
          <TitleWithTooltip
            title="Topics"
            tooltip="Tell us what you usually talk about to improve the results personalization"
          />
          <Textarea
            value={userTopics}
            onChange={(e) => setUserTopics(e.target.value)}
            maxW={500}
            placeholder="Building in public"
            borderRadius="lg"
          />
        </FormControl>
      </Flex>

      {/* FILTERS  */}

      <Flex alignItems="center" pb="5" pt="10">
        <Text
          textStyle={textStyle["h2.bold.standard"]}
          color="#2D64BC"
          size="2xl"
        >
          Filter search results
        </Text>
        <Button
          variant="transparent"
          color="#2D64BC"
          onClick={resetSettings}
          size="sm"
          pl="5"
        >
          Reset Filters
        </Button>
      </Flex>

      <Flex gap="3" wrap="wrap">
        <Flex direction="column">
          <TitleWithTooltip
            title="Platform"
            tooltip="Choose the platform you want to see your posts from"
          />

          <ButtonGroup
            variant="light"
            isAttached
            py={2}
            borderLeft="1"
            borderRight="1"
          >
            {plateformOptions.map(({ value, label }) => {
              return (
                <Filter
                  key={value}
                  type="button"
                  title={label}
                  value={value}
                  isActive={value === props.plateformFilter}
                  setValue={() => props.setPlateformFilter(value)}
                />
              );
            })}
          </ButtonGroup>
        </Flex>

        <Flex direction="column">
          <TitleWithTooltip
            title="Carousel posts"
            tooltip="Choose if you want to see posts with a carousel or not"
          />
          <ButtonGroup
            variant="light"
            isAttached
            py={2}
            borderLeft="1"
            borderRight="1"
          >
            {carrouselOptions.map(({ value, label }) => {
              return (
                <Filter
                  key={value}
                  type="button"
                  title={label}
                  value={value}
                  isActive={value === props.includeCarousel}
                  setValue={() => props.setIncludeCarousel(value)}
                />
              );
            })}
          </ButtonGroup>
        </Flex>

        <Flex direction="column">
          <TitleWithTooltip
            title="Hashtags in post"
            tooltip="Choose if you want to see posts with hashtags or not"
          />
          <ButtonGroup variant="light" isAttached py={2}>
            {hashtagOptions.map(({ value, label }) => {
              return (
                <Filter
                  key={value}
                  type="button"
                  title={label}
                  value={value}
                  isActive={value === props.noHashtag}
                  setValue={() => props.setNoHashtag(value)}
                />
              );
            })}
          </ButtonGroup>
        </Flex>
      </Flex>
      <Flex gap="3" pt="5" flexDirection={{ base: "column", md: "row" }}>
        {getAccount(session)?.id && props.countrySelected && (
          <>
            <InputSelectRadio
              setMaximumDateFilter={props.setMaximumDateFilter}
              maximumDateFilter={props.maximumDateFilter}
              options={lastInputDateOptions}
            />
            {/* <InputCountrySelect
                setCountrySelected={props.setCountrySelected}
                countrySelected={props.countrySelected}
              /> */}
          </>
        )}
      </Flex>

      <Text
        textStyle={textStyle["h2.bold.standard"]}
        color="#2D64BC"
        size="2xl"
        pb="1"
        pt="10"
      >
        Exclusion list
      </Text>

      <Text textStyle={textStyle["body.medium.primary"]} color="#5B6E8C">
        Do not show posts from these accounts.
      </Text>
      <Box minH="55px">
        <Flex
          pt="3"
          gap="3"
          wrap="wrap"
        // minH={55}
        >
          {props.exludedUsers &&
            props.exludedUsers.map((user) => (
              <Card
                direction="row"
                alignItems="baseline"
                pl="1"
                backgroundColor="#748094"
                color="white"
              >
                @{user}
                <CloseButton
                  size="sm"
                  onClick={() =>
                    props.setExcludedUsers((prevExcludedUsers) =>
                      prevExcludedUsers.filter(
                        (previousUser) => previousUser !== user
                      )
                    )
                  }
                />
              </Card>
            ))}
        </Flex>
      </Box>

      <Flex justifyContent="end" gap="3" mt="10">
        <Button variant="secondary" onClick={props.closeSettings}>
          Close
        </Button>
        <Button variant="primary" onClick={saveUpdates}>
          Apply Filters
        </Button>
      </Flex>
    </Card>
  );
};
